import React, { useState, useMemo, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { EmailRegex, InputChangeVariant, InputProps, ErrorMessage } from './Input.types';
import styles from './Input.module.scss';
import { EyeClosed, EyeOpen } from '@ds-icons';

export const Input = (props: InputProps) => {
  const {
    label,
    required,
    className,
    id,
    icon,
    disabled,
    hasChanged = false,
    highlightOnChange = true,
    placeholder = '',
    iconPosition = 'left',
    iconClickable = false,
    debounceTime,
    ref,
    onIconClick,
    validate,
    customErrorMessage,
    inputChangedStyle = InputChangeVariant.Default,
    hideErrorMessage = false,
    showErrorBorder,
    showEyeIcon,
    ...restProps
  } = props;
  const internalRef = useRef<HTMLInputElement>(null);
  const inputRef = ref || internalRef;
  const [touched, setTouched] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [inputValue, setInputValue] = useState<string>(restProps.value || '');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const inputId = useMemo(() => id || `input-${Math.random().toString(36).substring(2, 9)}`, [id]);
  const isHavingError = !isValid && touched;
  const inputClassName = classNames(className, styles['ds-input'], {
    [`${styles['input-error']}`]: showErrorBorder || (showErrorBorder === undefined && isHavingError),
  });
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const isPasswordField = restProps.type === 'password';

  useEffect(() => {
    setInputValue(restProps.value || '');
  }, [restProps.value]);

  useEffect(() => {
    if (touched) checkValidity();
  }, [validate]);

  const getClassNames = () => {
    return `${inputClassName} ${!!restProps.value ? 'input-filled' : ''} ${
      highlightOnChange && !isHavingError && hasChanged ? `input-changed ${inputChangedStyle}` : ''
    }`;
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const checkValidity = () => {
    if (!inputRef || !(inputRef as React.RefObject<HTMLInputElement>).current) return;

    const input = (inputRef as React.RefObject<HTMLInputElement>).current!;
    const isEmail = restProps.type === 'email';
    const inputValidity = input.validity;

    let isValid = input.checkValidity();

    if (isEmail) {
      const isEmailValid = EmailRegex.test(input.value);
      isValid = isValid && isEmailValid;
    }

    if (validate) {
      isValid = validate(input.value);
    }

    setIsValid(isValid);

    if (!isValid) {
      let errorMsg = '';

      if (customErrorMessage) {
        errorMsg = typeof customErrorMessage === 'function' ? customErrorMessage(input.value) : customErrorMessage;
      } else if (inputValidity.valueMissing) {
        errorMsg = ErrorMessage.FieldRequired;
      } else if (inputValidity.patternMismatch) {
        errorMsg = ErrorMessage.PatternMismatch;
      } else if (isEmail && !EmailRegex.test(input.value)) {
        errorMsg = ErrorMessage.InvalidEmail;
      } else {
        errorMsg = ErrorMessage.InvalidValue;
      }

      setErrorMessage(errorMsg);
    }
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;

    setTouched(true);
    const input = event.target as HTMLInputElement;

    if (restProps.max && restProps.type === 'number') {
      input.value = input.value.slice(0, 4);
    }
    setInputValue(event.target.value);

    checkValidity();

    if (restProps?.onChange) {
      /**
       * - If a debounce time is specified, delays the execution of the `onChange` callback by the given debounce time (in milliseconds) to avoid triggering the function immediately on every keystroke.
       * - If no debounce time is set, the `onChange` callback is executed immediately.
       */
      if (debounceTime && debounceTime > 0) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          if (restProps?.onChange) restProps.onChange(event);
        }, debounceTime);
      } else {
        restProps.onChange(event);
      }
    }
  };

  const InputElement = (
    <InputText
      {...restProps}
      ref={inputRef}
      required={required}
      disabled={disabled}
      id={inputId}
      value={inputValue}
      className={getClassNames()}
      placeholder={placeholder}
      onChange={onInputChange}
      onBlur={(event) => {
        setTouched(true);
        if (restProps?.onBlur) restProps.onBlur(event);
      }}
      type={showPassword ? 'text' : restProps.type}
      data-testid={isPasswordField ? 'password-input' : 'text-input'}
    />
  );

  if (!label && !icon) {
    return InputElement;
  }

  return (
    <div className={classNames(styles['ds-input-wrapper'], { [styles['ds-disabled-input-wrapper']]: disabled })}>
      {label && (
        <label htmlFor={inputId}>
          {label} {required && <span className="required-asterisk">*</span>}
        </label>
      )}
      {icon ? (
        <IconField data-testid="icon-field" iconPosition={iconPosition}>
          <InputIcon
            className={`${iconClickable ? 'cursor-pointer' : ''}`}
            onClick={() => {
              if (iconClickable) onIconClick?.();
            }}
          >
            {icon}
          </InputIcon>
          {InputElement}
        </IconField>
      ) : (
        InputElement
      )}
      {isPasswordField && showEyeIcon && inputValue && (
        <div className={styles['eye-icon-wrapper']} onClick={togglePasswordVisibility} data-testid="eye-icon-wrapper">
          <img
            className={styles['eye-icon']}
            src={showPassword ? EyeClosed : EyeOpen}
            data-testid="password-visibility-icon"
            alt={showPassword ? 'Hide password' : 'Show password'}
          />
        </div>
      )}

      {!hideErrorMessage && isHavingError && errorMessage && (
        <span className="error-message" data-testid="error-message">
          {errorMessage}
        </span>
      )}
    </div>
  );
};
